import { useEffect, useRef, useState } from "react";
import { ChevronsRight, Loader2 } from "lucide-react";

const THRESHOLD = 0.95;
const KEY_STEP = 0.04;

export const SlideToAccept = ({ onAccept, disabled, busy, testId, label = "Slide to accept" }) => {
  const track = useRef(null);
  const drag = useRef(null);
  const posRef = useRef(0);
  const [pos, setPosState] = useState(0);
  const setPos = (v) => {
    const next = typeof v === "function" ? v(posRef.current) : v;
    posRef.current = next;
    setPosState(next);
  };
  const [animating, setAnimating] = useState(false);
  const [w, setW] = useState(1);
  useEffect(() => {
    const ro = new ResizeObserver(() => setW(Math.max(1, (track.current?.clientWidth || 57) - 56)));
    ro.observe(track.current);
    return () => ro.disconnect();
  }, []);

  const at = (clientX) => Math.min(1, Math.max(0, drag.current.startPos + (clientX - drag.current.startX) / w));
  const finish = (p) => {
    drag.current = null;
    if (p >= THRESHOLD && !disabled) { setPos(1); onAccept(); return; }
    setAnimating(true);
    setPos(0);
    setTimeout(() => setAnimating(false), 250);
  };
  const down = (e) => {
    if (disabled || busy) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startX: e.clientX, startPos: posRef.current };
    setAnimating(false);
  };
  const move = (e) => {
    if (!drag.current) return;
    setPos(at(e.clientX));
  };
  const up = (e) => {
    if (!drag.current) return;
    const final = Number.isFinite(e?.clientX) && e.type === "pointerup" ? Math.max(posRef.current, at(e.clientX)) : posRef.current;
    finish(final);
  };
  const keyDown = (e) => {
    if (disabled || busy || e.key !== "ArrowRight") return;
    e.preventDefault();
    setAnimating(false);
    setPos((p) => Math.min(1, p + KEY_STEP));
  };
  const keyUp = (e) => { if (e.key === "ArrowRight") finish(posRef.current); };

  return (
    <div ref={track} data-testid={testId} data-position={Math.round(pos * 100)}
      className={`relative h-14 w-full select-none overflow-hidden rounded-full border border-amber-400/50 bg-amber-400/10 ${disabled ? "opacity-50" : ""}`}>
      <div className={`absolute inset-y-0 left-0 bg-gradient-to-r from-amber-500/40 to-amber-300/60 ${animating ? "transition-[width] duration-200" : ""}`} style={{ width: `${pos * w + 56}px` }} />
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center pl-10 text-sm font-semibold tracking-wide text-amber-100">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `${label} »`}
      </span>
      <button type="button" role="slider" aria-label={`${label} — hold Right Arrow`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pos * 100)}
        data-testid={`${testId}-handle`} disabled={disabled || busy}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onKeyDown={keyDown} onKeyUp={keyUp}
        className={`absolute left-0 top-0 flex h-14 w-14 touch-none cursor-grab items-center justify-center rounded-full bg-gradient-to-br from-amber-200 to-amber-500 text-black shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-200 active:cursor-grabbing ${animating ? "transition-transform duration-200" : ""}`}
        style={{ transform: `translateX(${pos * w}px)` }}>
        <ChevronsRight className="h-6 w-6" />
      </button>
    </div>
  );
};
